import { DOCUMENT } from '@angular/common';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Inject,
  ViewChild,
  ElementRef,
  SimpleChanges,
} from '@angular/core';
import { Filter } from '../../types/table.type';
import { timeoutGuard } from '../../utils/shared';

@Component({
  selector: 'hospitality-bot-custom-tab-header',
  templateUrl: './custom-tab-header.component.html',
  styleUrls: ['./custom-tab-header.component.scss'],
})
export class CustomTabHeaderComponent implements OnInit {
  @Input() listItems: Filter<string, string>[] = [];
  @Input() selectedIndex = 0;
  @Input() isLoading: boolean;
  @Input() isContent = false;
  @Input() isScrollable = true;
  @Input() view: 'vertical' | 'horizontal' = 'vertical';
  @Output() selectedTabChange = new EventEmitter();
  @ViewChild('tabFilter') tabFilter: ElementRef;
  @Input() extraGap = 40;
  @Input() scrollWidth = 300;

  @ViewChild('tabContainer') tabContainer: ElementRef;
  isTabOverflow = false;
  resizeHandler: (() => void) | null = null;
  currentScrollPosition = 0;
  disableNext = false;
  disablePrev = true;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  @Input() isSticky = false;
  isScrolledUp: boolean = false;

  ngOnInit(): void {
    this.document
      .getElementById('main-layout')
      ?.addEventListener('scroll', this.onScroll);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset selectedIndex to 0 when listItems change
    if (
      changes.listItems.previousValue?.length !==
      changes.listItems.currentValue?.length
    ) {
      timeoutGuard(()=>{
        this.checkTabOverflow()
      }, 100)
      this.selectedIndex = 0;
      this.selectedTabChange.emit(this.listItems[0]?.value);
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.checkTabOverflow();
      this.addResizeListener();
    });
  }

  onScroll = () => {
    const tabFilterElement = this.tabFilter?.nativeElement;
    if (tabFilterElement) {
      const { top } = tabFilterElement.getBoundingClientRect();
      this.isScrolledUp = top < 120;
    }
  };

  selectTab(index: number): void {
    if (!this.listItems[index]?.disabled) {
      this.selectedIndex = index;
      this.selectedTabChange.emit(this.listItems[index].value);
    }
  }

  scrollTabs(direction: 'prev' | 'next') {
    if (!this.tabContainer) return;

    const container = this.tabContainer.nativeElement;
    const scrollAmount = container.clientWidth * 0.8; // Scroll 80% of container width

    if (direction === 'next') {
      this.currentScrollPosition += scrollAmount;
      // Ensure we don't scroll beyond the total width
      this.currentScrollPosition = Math.min(
        this.currentScrollPosition,
        container.scrollWidth - container.clientWidth
      );
    } else {
      this.currentScrollPosition -= scrollAmount;
      // Ensure we don't scroll before the start
      this.currentScrollPosition = Math.max(this.currentScrollPosition, 0);
    }
    this.disableNext =
      this.currentScrollPosition + container.clientWidth ===
      container.scrollWidth
        ? true
        : false;
    this.disablePrev = this.currentScrollPosition === 0 ? true : false;

    container.scrollTo({
      left: this.currentScrollPosition,
      behavior: 'smooth',
    });
  }

  checkTabOverflow() {
    if (this.tabContainer) {
      const container = this.tabContainer.nativeElement;

      // More robust overflow detection
      this.isTabOverflow =
        container.scrollWidth > container.clientWidth ||
        container.offsetWidth < container.scrollWidth;
    }
  }

  addResizeListener() {
    this.resizeHandler = () => {
      // Debounce the resize check to prevent excessive calls
      setTimeout(() => {
        this.checkTabOverflow();
      }, 200);
    };

    // Add resize listener to window
    window.addEventListener('resize', this.resizeHandler);
  }

  removeResizeListener() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
  }
}
